import { useEffect, useRef, useState } from "react";
import { uniq } from "lodash";
import { message } from "components";
import { AICC_SANDBOX_BASE_SCOPE, AiccSandboxBaseScope, AiccSandboxMode } from "constants/aiccPromptSandbox";
import { SANDBOX_MODE_BY_GROUP } from "./sandboxScope";
import { SandboxSavedPrompt, useSandboxSavesStore } from "./useSandboxSavesStore";
import { PROMPT_SAVED } from "./configs";

interface Flow {
  languageId?: number;
  mode: AiccSandboxMode;
  baseScope: AiccSandboxBaseScope;
  promptKeys: string[];
}

// 🔴 Before anything has rendered or run there is no list of the flow's prompts, so the saved prompt's tab stands in for it.
export const isSaveInFlow = (saved: SandboxSavedPrompt, { languageId, mode, baseScope, promptKeys }: Flow) =>
  saved.languageId === languageId &&
  baseScope === AICC_SANDBOX_BASE_SCOPE.working &&
  (promptKeys.length
    ? promptKeys.includes(saved.promptKey)
    : !!saved.group && SANDBOX_MODE_BY_GROUP[saved.group] === mode);

interface Options extends Flow {
  isOpen: boolean;
  isStarted: boolean;
  onSaved: () => void;
}

export function useSavedSinceRun(options: Options) {
  const [names, setNames] = useState<string[]>([]);
  const latest = useRef(options);
  latest.current = options;

  useEffect(
    () =>
      useSandboxSavesStore.subscribe(({ lastSaved }) => {
        const { isOpen, isStarted, onSaved } = latest.current;

        if (!lastSaved || !isSaveInFlow(lastSaved, latest.current)) {
          return;
        }

        if (isOpen) {
          message.info(PROMPT_SAVED(lastSaved.name));
        }

        onSaved();

        if (isStarted) {
          setNames((current) => uniq([...current, lastSaved.name]));
        }
      }),
    []
  );

  return { savedSinceRun: names, clearSavedSinceRun: () => setNames([]) };
}
